import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { toast } from "sonner";
import { LocationPermissionExplainer } from "@/components/permissions/LocationPermissionExplainer";

type PermissionStatus = "granted" | "denied" | "prompt" | "unknown";

const PERMISSION_STORAGE_KEY = "location_permission_status";

// Read cached status synchronously so the first render is already correct
const getInitialStatus = (): PermissionStatus => {
  const cached = localStorage.getItem(PERMISSION_STORAGE_KEY);
  if (cached === "granted" || cached === "denied" || cached === "prompt") {
    return cached;
  }
  return "unknown";
};

interface LocationPermissionContextValue {
  status: PermissionStatus;
  isGranted: boolean;
  isDenied: boolean;
  isLoading: boolean;
  requestPermission: () => Promise<boolean>;
  getCurrentPosition: () => Promise<GeolocationPosition | null>;
  openAppSettings: () => Promise<void>;
}

const LocationPermissionContext = createContext<LocationPermissionContextValue | null>(null);

interface LocationPermissionProviderProps {
  children: ReactNode;
}

export function LocationPermissionProvider({ children }: LocationPermissionProviderProps) {
  const [status, setStatus] = useState<PermissionStatus>(getInitialStatus);
  const [isLoading, setIsLoading] = useState(true);
  const [showExplainer, setShowExplainer] = useState(false);
  const [pendingResolve, setPendingResolve] = useState<((granted: boolean) => void) | null>(null);

  // Check initial permission status
  useEffect(() => {
    const checkPermission = async () => {
      if (Capacitor.isNativePlatform()) {
        // On native platforms, ALWAYS check actual permissions first
        // Don't rely on localStorage cache as user may have changed settings externally
        try {
          console.log("[Location] Checking native permissions...");
          const permResult = await Geolocation.checkPermissions();
          // Handle iOS-specific permission values: "whenInUse" and "always" are granted states
          const locationPerm = permResult.location as string;
          console.log("[Location] Native permission result:", locationPerm);
          
          const nativeStatus = 
            (locationPerm === "granted" || locationPerm === "whenInUse" || locationPerm === "always")
              ? "granted" 
              : locationPerm === "denied" 
                ? "denied" 
                : "prompt";
          
          setStatus(nativeStatus);
          localStorage.setItem(PERMISSION_STORAGE_KEY, nativeStatus);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error("[Location] Error checking native permissions:", error);
          // Retry once after 1 second in case Capacitor plugin wasn't fully initialized
          setTimeout(async () => {
            try {
              const retryResult = await Geolocation.checkPermissions();
              const retryPerm = retryResult.location as string;
              const retryStatus = 
                (retryPerm === "granted" || retryPerm === "whenInUse" || retryPerm === "always")
                  ? "granted" 
                  : retryPerm === "denied" 
                    ? "denied" 
                    : "prompt";
              setStatus(retryStatus);
              localStorage.setItem(PERMISSION_STORAGE_KEY, retryStatus);
            } catch {
              // Fall back to cache only on second failure; never show "Enable Location" for unknown
              const cachedStatus = localStorage.getItem(PERMISSION_STORAGE_KEY);
              if (cachedStatus === "granted" || cachedStatus === "denied" || cachedStatus === "prompt") {
                setStatus(cachedStatus as PermissionStatus);
              }
              // Leave status as the sync-initialized value from localStorage (or "unknown")
            } finally {
              setIsLoading(false);
            }
          }, 1000);
          // Don't call setIsLoading(false) here — the retry callback handles it
          return;
        }
      }
      
      // Web fallback
      const cachedStatus = localStorage.getItem(PERMISSION_STORAGE_KEY);
      
      if (!("permissions" in navigator)) {
        // If no permissions API, try cached status or unknown
        if (cachedStatus === "granted" || cachedStatus === "denied" || cachedStatus === "prompt") {
          setStatus(cachedStatus as PermissionStatus);
        } else {
          setStatus("unknown");
        }
        setIsLoading(false);
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
        setStatus(result.state as PermissionStatus);
        localStorage.setItem(PERMISSION_STORAGE_KEY, result.state);
        
        // Listen for changes
        result.addEventListener("change", () => {
          setStatus(result.state as PermissionStatus);
          localStorage.setItem(PERMISSION_STORAGE_KEY, result.state);
        });
      } catch {
        // Fall back to cached status
        if (cachedStatus === "granted" || cachedStatus === "denied" || cachedStatus === "prompt") {
          setStatus(cachedStatus as PermissionStatus);
        } else {
          setStatus("unknown");
        }
      }
      
      setIsLoading(false);
    };

    checkPermission();
  }, []);

  // Re-check permissions when app comes to foreground (user may have changed in Settings)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      
      try {
        console.log("[Location] App resumed, re-checking permissions...");
        const permResult = await Geolocation.checkPermissions();
        const locationPerm = permResult.location as string;
        const newStatus = 
          (locationPerm === "granted" || locationPerm === "whenInUse" || locationPerm === "always")
            ? "granted" 
            : locationPerm === "denied" 
              ? "denied" 
              : "prompt";
        
        if (newStatus !== status) {
          console.log("[Location] Permission changed:", status, "->", newStatus);
          setStatus(newStatus);
          localStorage.setItem(PERMISSION_STORAGE_KEY, newStatus);
        }
      } catch (error) {
        console.error("[Location] Error re-checking permissions:", error);
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [status]);

  const triggerSystemPrompt = useCallback(async (): Promise<boolean> => {
    if (Capacitor.isNativePlatform()) {
      try {
        const permResult = await Geolocation.requestPermissions();
        const locationPerm = permResult.location as string;
        // Handle iOS-specific permission values: "whenInUse" and "always" are granted states
        const granted = locationPerm === "granted" || locationPerm === "whenInUse" || locationPerm === "always";
        const newStatus = granted ? "granted" : "denied";
        setStatus(newStatus);
        localStorage.setItem(PERMISSION_STORAGE_KEY, newStatus);
        return granted;
      } catch (error) {
        console.error("Error requesting native permissions:", error);
        setStatus("denied");
        localStorage.setItem(PERMISSION_STORAGE_KEY, "denied");
        return false;
      }
    }
    
    // Web fallback
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => {
          setStatus("granted");
          localStorage.setItem(PERMISSION_STORAGE_KEY, "granted");
          resolve(true);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setStatus("denied");
            localStorage.setItem(PERMISSION_STORAGE_KEY, "denied");
          }
          resolve(false);
        },
        { enableHighAccuracy: false, timeout: 10000 }
      );
    });
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    // If already granted, resolve immediately
    if (status === "granted") {
      return true;
    }

    // If denied, can't request again
    if (status === "denied") {
      return false;
    }

    // For native platforms, re-check actual permission status before showing explainer
    // This handles race conditions where status might not have updated yet
    if (Capacitor.isNativePlatform()) {
      try {
        const permResult = await Geolocation.checkPermissions();
        const locationPerm = permResult.location as string;
        console.log("[Location] Re-checking before explainer:", locationPerm);
        
        if (locationPerm === "granted" || locationPerm === "whenInUse" || locationPerm === "always") {
          setStatus("granted");
          localStorage.setItem(PERMISSION_STORAGE_KEY, "granted");
          return true; // Already granted, no need for explainer!
        }
      } catch (error) {
        console.error("[Location] Error re-checking permissions:", error);
      }
    }

    // Show explainer first, then trigger system prompt
    return new Promise((resolve) => {
      setPendingResolve(() => resolve);
      setShowExplainer(true);
    });
  }, [status]);

  const handleExplainerAllow = useCallback(async () => {
    setShowExplainer(false);
    const granted = await triggerSystemPrompt();
    if (pendingResolve) {
      pendingResolve(granted);
      setPendingResolve(null);
    }
  }, [triggerSystemPrompt, pendingResolve]);

  const getCurrentPosition = useCallback(async (): Promise<GeolocationPosition | null> => {
    // Don't try to get location if permission is explicitly denied
    // For "prompt" status, we'll try to get location which will trigger the native permission dialog
    if (status === "denied") {
      return null;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        // First try with high accuracy
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
        });
        
        // Update status if we successfully got position
        if (status !== "granted") {
          setStatus("granted");
          localStorage.setItem(PERMISSION_STORAGE_KEY, "granted");
        }
        
        // Convert Capacitor position to standard GeolocationPosition format
        return {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        } as GeolocationPosition;
      } catch (error) {
        console.error("High accuracy location failed, trying lower accuracy:", error);
        
        // Retry with lower accuracy if high accuracy times out
        try {
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 30000,
          });
          
          if (status !== "granted") {
            setStatus("granted");
            localStorage.setItem(PERMISSION_STORAGE_KEY, "granted");
          }
          
          return {
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
          } as GeolocationPosition;
        } catch (retryError) {
          console.error("Location fetch failed:", retryError);
          return null;
        }
      }
    }

    // Web fallback
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (status !== "granted") {
            setStatus("granted");
            localStorage.setItem(PERMISSION_STORAGE_KEY, "granted");
          }
          resolve(position);
        },
        (error) => {
          console.error("Web geolocation error:", error);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    });
  }, [status]);

  const openAppSettings = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      
      if (platform === "ios") {
        // On iOS, we can open the app's settings page directly
        // Using window.location for URL scheme navigation
        window.location.href = "app-settings:";
      } else if (platform === "android") {
        // On Android, show instructions since we can't reliably open settings
        toast.info("Open Settings manually", {
          description: "Go to Settings → Apps → This App → Permissions → Location",
          duration: 6000,
        });
      }
    } else {
      // Web: show instructions since we can't open settings
      toast.info("Enable location in browser", {
        description: "Click the lock icon in your browser's address bar to manage location permissions.",
        duration: 6000,
      });
    }
  }, []);

  const value: LocationPermissionContextValue = {
    status,
    isGranted: status === "granted",
    isDenied: status === "denied",
    isLoading,
    requestPermission,
    getCurrentPosition,
    openAppSettings,
  };

  return (
    <LocationPermissionContext.Provider value={value}>
      {children}
      <LocationPermissionExplainer
        open={showExplainer}
        onClose={() => {
          setShowExplainer(false);
          if (pendingResolve) {
            pendingResolve(false);
            setPendingResolve(null);
          }
        }}
        onAllow={handleExplainerAllow}
      />
    </LocationPermissionContext.Provider>
  );
}

export function useLocationPermission() {
  const context = useContext(LocationPermissionContext);
  
  if (!context) {
    throw new Error("useLocationPermission must be used within a LocationPermissionProvider");
  }
  
  return context;
}
